<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  buildHandoffPlanSummary,
  buildReplayPlanSummary,
  operationStatusTone,
  redactedViewerKeys,
} from '../agentOperations';

const props = defineProps({
  runs: {
    type: Array,
    default: () => [],
  },
  title: {
    type: String,
    default: '',
  },
  emptyLabel: {
    type: String,
    required: true,
  },
  missingResponseLabel: {
    type: String,
    required: true,
  },
  cancelLabel: {
    type: String,
    default: 'Cancel',
  },
  canCancel: {
    type: Boolean,
    default: false,
  },
  canViewDetails: {
    type: Boolean,
    default: false,
  },
  selectedRun: {
    type: Object,
    default: null,
  },
  loadingRunId: {
    type: Number,
    default: null,
  },
  detailsLabel: {
    type: String,
    default: 'Details',
  },
  closeDetailsLabel: {
    type: String,
    default: 'Close details',
  },
  messagesLabel: {
    type: String,
    default: 'Messages',
  },
  actionsLabel: {
    type: String,
    default: 'Actions',
  },
  approvalsLabel: {
    type: String,
    default: 'Approvals',
  },
});

const emit = defineEmits(['cancel', 'view']);
const { t } = useI18n();

const runStatusLabels = {
  queued: t('CAPTAIN_EXPERIENCE.RUN_STATUS.QUEUED'),
  running: t('CAPTAIN_EXPERIENCE.RUN_STATUS.RUNNING'),
  waiting_for_approval: t('CAPTAIN_EXPERIENCE.RUN_STATUS.WAITING_FOR_APPROVAL'),
  completed: t('CAPTAIN_EXPERIENCE.RUN_STATUS.COMPLETED'),
  failed: t('CAPTAIN_EXPERIENCE.RUN_STATUS.FAILED'),
  cancelled: t('CAPTAIN_EXPERIENCE.RUN_STATUS.CANCELLED'),
};

const runSourceLabels = {
  playground: t('CAPTAIN_EXPERIENCE.RUN_SOURCE.PLAYGROUND'),
  conversation: t('CAPTAIN_EXPERIENCE.RUN_SOURCE.CONVERSATION'),
  maisveloz_solicitacoes_group: t(
    'CAPTAIN_EXPERIENCE.RUN_SOURCE.MAISVELOZ_SOLICITACOES_GROUP'
  ),
};

const approvalStatusLabels = {
  pending: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.PENDING'),
  approved: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.APPROVED'),
  rejected: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.REJECTED'),
  expired: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.EXPIRED'),
  cancelled: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.CANCELLED'),
};

const runStatusLabel = run =>
  runStatusLabels[run.status] || run.status_label || run.status;
const runSourceLabel = run =>
  runSourceLabels[run.source] || run.source_label || run.source;
const toolStatusLabel = action =>
  runStatusLabels[action.status] || action.status_label || action.status;
const approvalStatusLabel = approval =>
  approvalStatusLabels[approval.status] ||
  approval.status_label ||
  approval.status;

const messageLine = message =>
  `${message.sender_name || message.role}: ${message.content}`;
const actionLine = action => `${action.name} — ${toolStatusLabel(action)}`;
const approvalLine = approval =>
  `${approval.requested_action} — ${approvalStatusLabel(approval)}`;

const isCancellable = run =>
  ['queued', 'running', 'waiting_for_approval'].includes(run.status);

const hasPayload = value => {
  if (!value) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return String(value).trim().length > 0;
};

const countFor = value => (Array.isArray(value) ? value.length : 0);

const selectedRunStats = computed(() => {
  const run = props.selectedRun || {};
  return [
    {
      key: 'messages',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_MESSAGES'),
      value: countFor(run.messages),
    },
    {
      key: 'actions',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_ACTIONS'),
      value: countFor(run.actions),
    },
    {
      key: 'approvals',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_APPROVALS'),
      value: countFor(run.approvals),
    },
  ];
});

const approvalForAction = (run, action) =>
  (run?.approvals || []).find(
    approval =>
      approval.agent_tool_call_id === action.id ||
      approval.id === action.approval_id
  );

const evidencePayload = record => {
  if (hasPayload(record?.evidence)) return record.evidence;
  const metadata = record?.metadata || {};
  return (
    metadata.evidence ||
    metadata.evidences ||
    metadata.sources ||
    metadata.grounding ||
    metadata.context_resolution ||
    metadata.resolved_context ||
    metadata.consent ||
    null
  );
};

const fallbackItems = run => {
  if (!run) return [];
  const items = [];
  if (run.error_message) {
    items.push({
      key: 'run-error',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_ERROR'),
      value: run.error_message,
      tone: 'danger',
    });
  }
  if (run.handoff_tool_called) {
    items.push({
      key: 'handoff',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_HANDOFF'),
      value: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_HANDOFF_DESCRIPTION'),
      tone: 'info',
    });
  }
  if (run.status === 'waiting_for_approval') {
    items.push({
      key: 'approval',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_APPROVAL_BLOCKED'),
      value: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_APPROVAL_BLOCKED_DESCRIPTION'),
      tone: 'warning',
    });
  }
  (run.actions || []).forEach(action => {
    if (action.error_message) {
      items.push({
        key: `action-error-${action.id}`,
        label: action.name,
        value: action.error_message,
        tone: 'danger',
      });
    }
    if (action.retry_blocked_reason) {
      items.push({
        key: `retry-blocked-${action.id}`,
        label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_RETRY_BLOCKED'),
        value: action.retry_blocked_reason,
        tone: 'warning',
      });
    } else if (action.manual_retry_allowed) {
      items.push({
        key: `retry-allowed-${action.id}`,
        label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_RETRY_ALLOWED'),
        value: action.name,
        tone: 'info',
      });
    }
  });
  if (run.replay_error) {
    items.push({
      key: 'replay-error',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_REPLAY_PLAN'),
      value: run.replay_error,
      tone: 'warning',
    });
  }
  if (run.handoff_error) {
    items.push({
      key: 'handoff-error',
      label: t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_HANDOFF_PLAN'),
      value: run.handoff_error,
      tone: 'warning',
    });
  }
  return items;
};

const timestampLabel = value => {
  if (!value) return '—';
  const numericValue = Number(value);
  const date = new Date(
    Number.isFinite(numericValue) ? numericValue * 1000 : value
  );
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const planRows = plan => buildReplayPlanSummary(plan, t);
const handoffRows = plan => buildHandoffPlanSummary(plan, t);
</script>

<template>
  <section class="flex flex-col gap-3">
    <h2 v-if="title" class="text-base font-medium text-n-slate-12">
      {{ title }}
    </h2>
    <div v-if="runs.length" class="flex flex-col gap-2">
      <article
        v-for="run in runs"
        :key="run.id"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
      >
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ runSourceLabel(run) }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ runStatusLabel(run) }}
          </span>
        </div>
        <p class="mt-2 text-sm line-clamp-2 text-n-slate-11">
          {{ run.response || missingResponseLabel }}
        </p>
        <div class="mt-2 flex flex-wrap gap-2">
          <Button
            v-if="canViewDetails"
            :label="
              selectedRun?.id === run.id ? closeDetailsLabel : detailsLabel
            "
            color="slate"
            variant="ghost"
            size="xs"
            :is-loading="loadingRunId === run.id"
            @click="emit('view', run)"
          />
          <Button
            v-if="canCancel && isCancellable(run)"
            :label="cancelLabel"
            color="ruby"
            variant="ghost"
            size="xs"
            @click="emit('cancel', run)"
          />
        </div>
        <div
          v-if="selectedRun?.id === run.id"
          class="mt-3 flex flex-col gap-4 rounded-lg border border-n-weak bg-n-alpha-2 p-3 text-xs text-n-slate-11"
        >
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div
              v-for="item in selectedRunStats"
              :key="item.key"
              class="rounded-md border border-n-weak bg-n-solid-2 px-3 py-2"
            >
              <p class="text-[0.7rem] font-medium uppercase text-n-slate-10">
                {{ item.label }}
              </p>
              <p class="mt-1 text-base font-semibold text-n-slate-12">
                {{ item.value }}
              </p>
            </div>
          </div>

          <div
            v-if="fallbackItems(selectedRun).length"
            class="flex flex-col gap-2"
          >
            <strong class="text-n-slate-12">
              {{ t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_FALLBACK') }}
            </strong>
            <div
              v-for="item in fallbackItems(selectedRun)"
              :key="item.key"
              class="flex flex-col gap-1 rounded-md border border-n-weak bg-n-solid-2 p-3"
            >
              <div class="flex flex-wrap items-center gap-2">
                <LaisStatusBadge
                  :label="item.label"
                  :tone="item.tone"
                  size="sm"
                  dot
                />
              </div>
              <p class="break-words text-n-slate-11">{{ item.value }}</p>
            </div>
          </div>

          <div
            v-if="selectedRun.summary || selectedRun.metrics"
            class="grid grid-cols-1 gap-3 lg:grid-cols-2"
          >
            <LaisPayloadViewer
              v-if="hasPayload(selectedRun.summary)"
              :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_SUMMARY')"
              :payload="selectedRun.summary"
              :redacted-keys="redactedViewerKeys"
              :can-copy="false"
              :max-depth="2"
            />
            <LaisPayloadViewer
              v-if="hasPayload(selectedRun.metrics)"
              :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_METRICS')"
              :payload="selectedRun.metrics"
              :redacted-keys="redactedViewerKeys"
              :can-copy="false"
              :max-depth="2"
            />
          </div>

          <div
            v-if="selectedRun.replay_plan || selectedRun.handoff_plan"
            class="grid grid-cols-1 gap-3 xl:grid-cols-2"
          >
            <div
              v-if="selectedRun.replay_plan"
              class="rounded-md border border-n-weak bg-n-solid-2 p-3"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <strong class="text-n-slate-12">
                  {{ t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_REPLAY_PLAN') }}
                </strong>
                <LaisStatusBadge
                  :label="t('CAPTAIN_EXPERIENCE.OPERATIONS.DRY_RUN')"
                  tone="info"
                  size="sm"
                  icon="i-lucide-eye"
                />
              </div>
              <dl class="mt-3 grid gap-2 sm:grid-cols-2">
                <div
                  v-for="row in planRows(selectedRun.replay_plan)"
                  :key="row.key"
                  class="min-w-0 rounded bg-n-alpha-2 p-2"
                >
                  <dt
                    class="text-[0.7rem] font-medium uppercase text-n-slate-10"
                  >
                    {{ row.label }}
                  </dt>
                  <dd class="mt-1 break-words text-n-slate-12">
                    {{ row.value }}
                  </dd>
                </div>
              </dl>
              <LaisPayloadViewer
                class="mt-3"
                :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_REPLAY_PAYLOAD')"
                :payload="selectedRun.replay_plan"
                :redacted-keys="redactedViewerKeys"
                :can-copy="false"
                :max-depth="2"
              />
            </div>

            <div
              v-if="selectedRun.handoff_plan"
              class="rounded-md border border-n-weak bg-n-solid-2 p-3"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <strong class="text-n-slate-12">
                  {{ t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_HANDOFF_PLAN') }}
                </strong>
                <LaisStatusBadge
                  :label="t('CAPTAIN_EXPERIENCE.OPERATIONS.DRY_RUN')"
                  tone="info"
                  size="sm"
                  icon="i-lucide-eye"
                />
              </div>
              <dl class="mt-3 grid gap-2 sm:grid-cols-2">
                <div
                  v-for="row in handoffRows(selectedRun.handoff_plan)"
                  :key="row.key"
                  class="min-w-0 rounded bg-n-alpha-2 p-2"
                >
                  <dt
                    class="text-[0.7rem] font-medium uppercase text-n-slate-10"
                  >
                    {{ row.label }}
                  </dt>
                  <dd class="mt-1 break-words text-n-slate-12">
                    {{ row.value }}
                  </dd>
                </div>
              </dl>
              <LaisPayloadViewer
                class="mt-3"
                :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_HANDOFF_PAYLOAD')"
                :payload="selectedRun.handoff_plan"
                :redacted-keys="redactedViewerKeys"
                :can-copy="false"
                :max-depth="2"
              />
            </div>
          </div>

          <div v-if="selectedRun.messages?.length">
            <strong class="text-n-slate-12">{{ messagesLabel }}</strong>
            <ul class="mt-2 flex flex-col gap-2">
              <li
                v-for="message in selectedRun.messages"
                :key="message.id"
                class="rounded-md border border-n-weak bg-n-solid-2 p-3"
              >
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <span class="font-medium text-n-slate-12">
                    {{ messageLine(message) }}
                  </span>
                  <span class="text-n-slate-10">
                    {{ timestampLabel(message.created_at) }}
                  </span>
                </div>
                <LaisPayloadViewer
                  v-if="hasPayload(message.metadata)"
                  class="mt-2"
                  :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_EVIDENCE')"
                  :payload="message.metadata"
                  :redacted-keys="redactedViewerKeys"
                  :can-copy="false"
                  :max-depth="2"
                />
              </li>
            </ul>
          </div>
          <div v-if="selectedRun.actions?.length">
            <strong class="text-n-slate-12">{{ actionsLabel }}</strong>
            <ul class="mt-2 flex flex-col gap-2">
              <li
                v-for="action in selectedRun.actions"
                :key="action.id"
                class="rounded-md border border-n-weak bg-n-solid-2 p-3"
              >
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="font-medium text-n-slate-12">
                      {{ actionLine(action) }}
                    </p>
                    <p class="mt-1 text-n-slate-10">
                      {{
                        t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_TOOL_POLICY', {
                          permission: action.permission || '—',
                          risk: action.risk_level || '—',
                        })
                      }}
                    </p>
                  </div>
                  <LaisStatusBadge
                    :label="toolStatusLabel(action)"
                    :tone="operationStatusTone(action.status)"
                    size="sm"
                    dot
                  />
                </div>
                <p
                  v-if="approvalForAction(selectedRun, action)"
                  class="mt-2 rounded bg-n-alpha-2 p-2 text-n-amber-11"
                >
                  {{
                    t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_APPROVAL_LINKED', {
                      approval: approvalForAction(selectedRun, action)
                        .requested_action,
                      status: approvalStatusLabel(
                        approvalForAction(selectedRun, action)
                      ),
                    })
                  }}
                </p>
                <div class="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <LaisPayloadViewer
                    v-if="hasPayload(action.arguments)"
                    :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_TOOL_INPUT')"
                    :payload="action.arguments"
                    :redacted-keys="redactedViewerKeys"
                    :can-copy="false"
                    :max-depth="2"
                  />
                  <LaisPayloadViewer
                    v-if="hasPayload(action.result_payload)"
                    :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_TOOL_OUTPUT')"
                    :payload="action.result_payload"
                    :redacted-keys="redactedViewerKeys"
                    :can-copy="false"
                    :max-depth="2"
                  />
                </div>
                <p v-if="action.result" class="mt-2 break-words">
                  {{ action.result }}
                </p>
                <p
                  v-if="action.error_message"
                  class="mt-2 break-words text-n-ruby-11"
                >
                  {{ action.error_message }}
                </p>
                <LaisPayloadViewer
                  v-if="hasPayload(evidencePayload(action))"
                  class="mt-3"
                  :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_EVIDENCE')"
                  :payload="evidencePayload(action)"
                  :redacted-keys="redactedViewerKeys"
                  :can-copy="false"
                  :max-depth="2"
                />
              </li>
            </ul>
          </div>
          <div v-if="selectedRun.approvals?.length">
            <strong class="text-n-slate-12">{{ approvalsLabel }}</strong>
            <ul class="mt-2 flex flex-col gap-2">
              <li
                v-for="approval in selectedRun.approvals"
                :key="approval.id"
                class="rounded-md border border-n-weak bg-n-solid-2 p-3"
              >
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="font-medium text-n-slate-12">
                      {{ approvalLine(approval) }}
                    </p>
                    <p v-if="approval.reason" class="mt-1 break-words">
                      {{ approval.reason }}
                    </p>
                  </div>
                  <LaisStatusBadge
                    :label="approvalStatusLabel(approval)"
                    :tone="operationStatusTone(approval.status)"
                    size="sm"
                    dot
                  />
                </div>
                <div class="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <LaisPayloadViewer
                    v-if="hasPayload(approval.request_payload)"
                    :title="t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_APPROVAL_PAYLOAD')"
                    :payload="approval.request_payload"
                    :redacted-keys="redactedViewerKeys"
                    :can-copy="false"
                    :max-depth="2"
                  />
                  <LaisPayloadViewer
                    v-if="hasPayload(approval.decision_payload)"
                    :title="
                      t('CAPTAIN_EXPERIENCE.SCRIPT.RUN_APPROVAL_DECISION')
                    "
                    :payload="approval.decision_payload"
                    :redacted-keys="redactedViewerKeys"
                    :can-copy="false"
                    :max-depth="2"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </article>
    </div>
    <p v-else class="text-sm text-n-slate-11">
      {{ emptyLabel }}
    </p>
  </section>
</template>
